import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useEntityOverride } from '@hooks/auth/useEntityOverride';
import { isWithinSegment, RESET_SCOPE_STATE } from './scopedSegments';

/**
 * Clears the per-segment `entityOverride` once navigation leaves the segment it
 * was selected on — the portal2 equivalent of legacy's clear-on-unmount
 * (`portalnew` `EntitySelector/index.js:64-67`).
 *
 * Legacy wraps every route of a segment in one `EntitySelector`, so its cleanup
 * fires only on true segment-leave. portal2 hoists each segment's sub-routes to
 * sibling top-level routes, so an unmount-based clear would wipe scope on
 * intra-segment navigation (e.g. opening a plugin's detail). Instead the
 * override carries the prefix it was set on (`override.segment`, stamped by
 * `EntitySelector`), and this layout-level effect clears it when `pathname`
 * leaves that prefix. Mounted once in `AppLayout`, which wraps all routes.
 *
 * Semantics matched to legacy:
 *   - Intra-segment nav keeps the scope (path still within `segment`), including
 *     navigating from a sub-route back to the segment root.
 *   - F5 keeps the scope on the same segment (override + segment rehydrate from
 *     localStorage and the path still matches) but clears it if the reload
 *     landed on a non-owning page (a stale persisted scope is dropped).
 *   - `autoSelected` overrides have no `segment` (a regular merchant/vendor's
 *     own identity) and are never cleared — they persist across every page.
 *   - A navigation stamped with `RESET_SCOPE_STATE` (any main-menu/sidebar
 *     click — see `Sidebar`) clears the manual scope even when it stays INSIDE
 *     the segment, so re-clicking the active section returns the user to the
 *     merchant/vendor picker instead of being stuck on the old account.
 *     In-segment pushes without the marker (Cancel /
 *     back-to-list buttons, the picker's own picked-content entry) still keep
 *     the scope.
 */
export function useEntityScopeReset(): void {
  const { pathname, state, key } = useLocation();
  const { entityOverride, clearEntityOverride } = useEntityOverride();
  const segment = entityOverride?.segment;
  const isResetNav = Boolean((state as Record<string, unknown> | null)?.[RESET_SCOPE_STATE]);

  // The reset marker is consumed ONCE per history entry (`key` identifies it).
  // The effect also re-runs when the OVERRIDE changes while the location stays
  // put — exactly what happens when the user picks an entity on a picker
  // reached via the sidebar (Redux commits before EntitySelector's pushed
  // content entry lands). Re-applying the marker then would clear the
  // brand-new pick, so a key already handled never marker-clears again.
  const handledKey = useRef<string | null>(null);

  useEffect(() => {
    const isNewEntry = handledKey.current !== key;
    handledKey.current = key;
    if (!segment) return; // no owning segment (none set, or autoSelected) → never clear

    if ((isResetNav && isNewEntry) || !isWithinSegment(pathname, segment)) {
      // Main-menu navigation, or navigated out of the segment — clear
      // (legacy clear-on-unmount parity; menu-click reset).
      clearEntityOverride();
    }
  }, [pathname, segment, clearEntityOverride, isResetNav, key]);
}
